import {Box} from "@mui/system";
import React, {useEffect, useState} from "react";
import {useParams} from "react-router";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {Document as IDocument} from "../../models/Document";
import {DocumentViewer} from "../document-viewer/DocumentViewer";
import {GET_PRESIGNED_URL} from "../graphql/queries/gqlGetPresignedUrl";
import {GET_DOCUMENT_BY_IDENTIFIER} from "../graphql/queries/gqlGetDocumentByIdentifier";
import {pageAuthorizedByGroup, isAuthorizedByAnyGroup} from "../../../fragments/PageAuthorization";
import {Alert, CircularProgress, Button} from "@mui/material";
import {DocumentCard} from "../document/DocumentCard";
import {DocumentActionsCard, mailDocumentType} from "../document/DocumentActionsCard";
import {GenericModal} from "../../../querytool/components/generic-modal/GenericModal";
import {MailDocumentCard} from "../mail/mailDocumentCard";
import {translateFunction} from "../../../../localization/TranslationService";
import {DocumentFile} from "../../models/DocumentFile";
import {DocumentFileType} from "../../models/DocumentFileType";
import {ReindexDocumentCard} from "../reindex/reindexDocumentCard";
import {ValidationSeverity} from "../../models/ValidationSeverity";
import {useNavigate} from "react-router-dom";

const translate = translateFunction("SureBaseDocuments");
const containerStyling: React.CSSProperties = {
	display: "flex",
	flexDirection: "row",
	width: "100%",
	height: "100%",
	maxHeight: "100%",
};

const documentViewerContainerStyling: React.CSSProperties = {
	width: "1px",
	height: "100%",
	maxHeight: "100%",
	position: "relative",
	flexGrow: 7,
};

function ViewDocumentFx() {
	const [data, setData] = useState<IDocument>();
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const {documentIdentifier} = useParams();
	const [errorMessage, setErrorMessage] = useState<string>();
	const [mailDocument, setMailDocument] = useState<mailDocumentType|undefined>();
	const [reindexDocument, setReindexDocument] = useState<boolean>(false);
	const [documentIsValid, setDocumentIsValid] = useState<boolean>(false);
	const navigate = useNavigate();
	const [navigateTo, setNavigateTo] = useState<string>();

	const fetch = async (identifier: string) => {
		setSubmitLoading(true);
		await graphQLServices.documentsClient.query({
			query: GET_DOCUMENT_BY_IDENTIFIER,
			variables: {
				identifier: identifier,
			},
		}).catch((reason) => setErrorMessage(reason.networkError.result.errors[0].extensions.message))
			.then((result: any) =>  {
				setData(result?.data?.documentByIdentifier);
				setDocumentIsValid(((data?.validations?.filter((it) => it.severity === ValidationSeverity.ERROR && it.field).length)??0) === 0);
			})
			.finally(() => setSubmitLoading(false))
		;



	};

	const getPresignedUrl = async (
		fileId: number,
		documentId: number,
		text: boolean
	): Promise<{url: string; fileName: string}> => {
		return (
			await graphQLServices.documentsClient.query({
				query: GET_PRESIGNED_URL,
				variables: {
					fileId,
					documentId,
					text
				},
				fetchPolicy: "network-only",
			})
		).data.filePresignedUrl;
	};

	useEffect(() => {
		if (documentIdentifier) fetch(documentIdentifier);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (navigateTo) {
		const nav = navigateTo;
		setNavigateTo(undefined);
		navigate(nav);
		return <></>;
	}

	if (submitLoading)
		return <CircularProgress></CircularProgress>;
	
	if (!data && (!errorMessage || errorMessage === "Unknown identifier")) {
		return <Alert severity={"warning"}>{translate("Document not found.")}</Alert>;
	}
	if (errorMessage)
		return <Alert severity={"error"}>{errorMessage}</Alert>;

	return (
		<div style={{height: "100%"}}>
			{data &&
			<Box style={containerStyling}>
				<Box style={documentViewerContainerStyling}>
					{mailDocument !== undefined && <GenericModal
						open={true}
						title={translate("Mail Document")}
						confirmClose={true}
						confirmTitle={translate("Close form")}
						confirmSuccessTitle={translate("Close")}
						confirmCancelTitle={translate("Cancel")}
						onClose={() => setMailDocument(undefined)}>
						<MailDocumentCard mailDocumentType={mailDocument} document={data}
							reloadDocument={() => fetch(data?.identifier)} onClose={() => setMailDocument(undefined)}/>
					</GenericModal>
					}
					{reindexDocument && <GenericModal open={true} title={translate("Reindex Document")} onClose={() => {
						setReindexDocument(false);
						fetch(data?.identifier);
					}}>
						<ReindexDocumentCard
							navigateToIdentifier={(identifier) => {
								setNavigateTo("../" + identifier + "?fullpage");
							}}
							documentIsValid={documentIsValid}
							document={JSON.parse(JSON.stringify(data))}
							reloadDocument={() => fetch(data?.identifier)}
							onClose={() => setReindexDocument(false)}/>
					</GenericModal>
					}
					
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
						<DocumentActionsCard
							document={data}
							documentIsValid={true}
							readOnly={true}
							mailDocument={(mailDocumentType) => setMailDocument(mailDocumentType)}
							viewer={true}
							reindex={() => setReindexDocument(true)}
							locked={false}
						/>
						{isAuthorizedByAnyGroup(["Admin", "Documents"]) && (
							<Button
								variant="contained"
								color="primary"
								onClick={() => navigate(`/documents/${data?.id}`)}
								sx={{ minWidth: "fit-content", whiteSpace: "nowrap" }}
							>
								{translate("Open in Documents")}
							</Button>
						)}
					</Box>
					
					<DocumentCard
						document={data}
					/>
					<DocumentViewer
						documentId={data?.id ?? 0}
						groupId={0}
						defaultDocumentId={data?.files?.filter(f => f.type !== DocumentFileType.DocumentSource).first().id}
						sourceDocument={(data?.files ?? []).filter(f => f.type === DocumentFileType.DocumentSource).map((it: DocumentFile) => {
							return {
								extension: it.extension ?? "",
								name: it.name ?? "",
								id: it.id,
								getPresignedUrl: async () =>
									await getPresignedUrl(it.id, +(data?.id ?? 0), false),
								groupId: it.groupId,
								parentGroupId: it.parentGroupId
							};
						}
						).firstOrNull() ?? undefined}
						documents={(data?.files ?? []).filter(f => f.type !== DocumentFileType.DocumentSource).map((it: DocumentFile) => {
							return {
								extension: it.extension ?? "",
								name: it.name ?? "",
								id: it.id,
								getPresignedUrl: async () =>
									await getPresignedUrl(it.id, +(data?.id ?? 0), it.extension === ".msg" || it.extension === ".eml"),
								groupId: it.groupId,
								parentGroupId: it.parentGroupId
							};
						}
						)}
					/>
				</Box>
			</Box>
			}
		</div>
	);
}

export const ViewDocument = pageAuthorizedByGroup(ViewDocumentFx, ["Admin", "Documents","DocumentsViewer"]);
